/**
 * /accounting/reports/job-profitability/:jobId — Job One-Pager.
 *
 * Steve's most-asked question, on one screen: where does THIS job stand right
 * now? Contract value, billed-to-date, deferred, WIP split (material/labor
 * including 1099 bills), AP committed, projected margin, every JE that
 * touched the job — all derived live from posted GL + AP + the job record.
 */
import { useMemo } from "react";
import { useLogReadOnMount } from "@/hooks/useLogReadOnMount";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Badge } from "@/components/ui/badge";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useAppData } from "@/data/AppDataContext";
import { useAP } from "@/data/accounting/ap/APContext";
import { getJobProfitability } from "@/data/accounting/reports/jobProfitability";
import { getContractorLaborForJob } from "@/data/accounting/ap/contractorLaborFromBills";

const fmt = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtPct = (n: number | null) => n === null ? "—" : `${(n * 100).toFixed(1)}%`;

export default function JobOnePagerPage() {
  useLogReadOnMount("job-one-pager", { sensitivity: "margin" });
  const { jobId = "" } = useParams();
  const { journalEntries, accounts } = useGeneralLedger();
  const { jobs } = useAppData();
  const { bills } = useAP();

  const job = jobs.find(j => j.id === jobId);

  const report = useMemo(
    () => getJobProfitability({
      journalEntries, accounts,
      customerNameByJobId: Object.fromEntries(jobs.map(j => [j.id, j.customerName])),
    }),
    [journalEntries, accounts, jobs],
  );
  const row = report.rows.find(r => r.jobId === jobId);

  const contractValue = job?.financials?.contractValue ?? 0;
  const invoices = job?.financials?.invoices ?? [];
  const billedToDate = invoices.reduce((s, i) => s + (i.amount || 0), 0);
  const unbilled = Math.max(0, contractValue - billedToDate);

  const contractorLabor = useMemo(() => getContractorLaborForJob(jobId, bills), [jobId, bills]);

  const jobBills = useMemo(
    () => bills.filter(b => b.status !== "void" && b.lines.some(l => l.jobId === jobId)),
    [bills, jobId],
  );
  const committed = jobBills.reduce((s, b) => s + b.lines
    .filter(l => l.jobId === jobId).reduce((ss, l) => ss + l.amount, 0), 0);

  const jeForJob = useMemo(
    () => journalEntries
      .filter(je => je.status === "posted" && je.lines.some(l => l.jobId === jobId))
      .sort((a, b) => b.postingDate.localeCompare(a.postingDate))
      .slice(0, 40),
    [journalEntries, jobId],
  );

  if (!job) {
    return (
      <CockpitPage>
        <BooksNavRail />
        <Link to="/accounting/reports/job-profitability" className="text-xs text-muted-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to Job Profitability
        </Link>
        <div className="mt-8 text-center text-muted-foreground">Job {jobId} not found.</div>
      </CockpitPage>
    );
  }

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-4">
        <Link to="/accounting/reports/job-profitability" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3 w-3" /> Back to Job Profitability
        </Link>
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">{job.customerName}</h1>
          <span className="font-mono text-sm text-muted-foreground">{job.jobNumber || job.id}</span>
          {job.installStatus && (
            <Badge variant="outline" className="text-xs">{job.installStatus}</Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">{job.projectType ?? "—"}</p>
      </div>

      {/* Money strip */}
      <CockpitSection>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Contract value" value={fmt(contractValue)} />
          <Tile label="Billed to date" value={fmt(billedToDate)} sub={`${invoices.length} invoice${invoices.length === 1 ? "" : "s"}`} />
          <Tile label="Unbilled" value={fmt(unbilled)} tone={unbilled > 0 ? "warn" : undefined} />
          <Tile label="Deferred revenue" value={fmt(row?.deferredRevenue ?? 0)} sub="On the BS until job closes" />
        </div>
      </CockpitSection>

      {/* Cost + margin */}
      <CockpitSection>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Material to date" value={fmt(row?.costMaterial ?? 0)} />
          <Tile
            label="Labor to date"
            value={fmt(row?.costLabor ?? 0)}
            sub={contractorLabor.dollars > 0
              ? `incl. ${fmt(contractorLabor.dollars)} 1099 (${contractorLabor.hours.toFixed(1)} hrs)`
              : undefined}
          />
          <Tile label="WIP outstanding" value={fmt(row?.wipBalance ?? 0)} sub="Capitalized, not yet COGS" />
          <Tile
            label="Gross margin"
            value={fmt(row?.grossMargin ?? 0)}
            sub={`${fmtPct(row?.grossMarginPct ?? null)} of revenue`}
            tone={(row?.grossMargin ?? 0) >= 0 ? "pos" : "neg"}
          />
        </div>
        <div className="mt-3 text-xs text-muted-foreground">
          Forecast margin (after WIP releases): <span className="font-medium tabular-nums text-foreground">{fmt(row?.forecastMargin ?? 0)}</span> ·
          {" "}AP committed against this job: <span className="font-medium tabular-nums text-foreground">{fmt(committed)}</span>
        </div>
      </CockpitSection>

      {/* Invoices */}
      <CockpitSection>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-medium">Invoices</h2>
          <span className="text-xs text-muted-foreground">{invoices.length} total</span>
        </div>
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr><Th>Invoice</Th><Th>Type</Th><Th>Status</Th><Th>Issued</Th><Th className="text-right">Amount</Th></tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No invoices yet.</td></tr>
              ) : invoices.map(inv => (
                <tr key={inv.id} className="border-t border-border">
                  <td className="p-2 font-mono">{inv.id}</td>
                  <td className="p-2 capitalize">{inv.type}</td>
                  <td className="p-2 capitalize"><Badge variant="outline" className="text-[10px]">{inv.status}</Badge></td>
                  <td className="p-2 text-muted-foreground">{inv.issuedAt?.slice(0, 10) ?? "—"}</td>
                  <td className="p-2 text-right tabular-nums">{fmt(inv.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CockpitSection>

      {/* AP bills against the job */}
      <CockpitSection>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-medium">AP bills against this job</h2>
          <span className="text-xs text-muted-foreground">{jobBills.length} total · {fmt(committed)} committed</span>
        </div>
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr><Th>Vendor</Th><Th>Invoice #</Th><Th>Date</Th><Th>Status</Th><Th className="text-right">Amount on job</Th></tr>
            </thead>
            <tbody>
              {jobBills.length === 0 ? (
                <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No bills posted against this job.</td></tr>
              ) : jobBills.map(b => {
                const onJob = b.lines.filter(l => l.jobId === jobId).reduce((s, l) => s + l.amount, 0);
                return (
                  <tr key={b.id} className="border-t border-border">
                    <td className="p-2">{b.vendorName}</td>
                    <td className="p-2 font-mono">{b.invoiceNumber}</td>
                    <td className="p-2 text-muted-foreground">{b.billDate}</td>
                    <td className="p-2 capitalize"><Badge variant="outline" className="text-[10px]">{b.status.replace("_", " ")}</Badge></td>
                    <td className="p-2 text-right tabular-nums">{fmt(onJob)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CockpitSection>

      {/* Journal entries */}
      <CockpitSection>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-medium inline-flex items-center gap-1.5"><FileText className="h-4 w-4" /> Journal entries touching this job</h2>
          <Link to={`/accounting/gl/journal-entries?jobId=${jobId}`} className="text-xs text-primary hover:underline">View all in GL →</Link>
        </div>
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr><Th>Date</Th><Th>JE</Th><Th>Source</Th><Th>Description</Th><Th className="text-right">Job impact</Th></tr>
            </thead>
            <tbody>
              {jeForJob.length === 0 ? (
                <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No journal activity yet.</td></tr>
              ) : jeForJob.map(je => {
                const impact = je.lines.filter(l => l.jobId === jobId)
                  .reduce((s, l) => s + (l.side === "debit" ? l.amount : -l.amount), 0);
                return (
                  <tr key={je.id} className="border-t border-border">
                    <td className="p-2 text-muted-foreground">{je.postingDate}</td>
                    <td className="p-2 font-mono">{je.id}</td>
                    <td className="p-2 capitalize text-muted-foreground">{je.source.replace(/_/g, " ")}</td>
                    <td className="p-2 truncate max-w-[280px]" title={je.description}>{je.description}</td>
                    <td className={`p-2 text-right tabular-nums ${impact < 0 ? "text-emerald-600" : ""}`}>
                      {impact >= 0 ? fmt(impact) : `(${fmt(-impact)})`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-[11px] text-muted-foreground mt-2">
          Job impact = net debit (positive = cost/AR going up; negative = release/payment).
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "pos" | "neg" | "warn" }) {
  const toneClass = tone === "pos" ? "text-emerald-600" : tone === "neg" ? "text-rose-600" : tone === "warn" ? "text-amber-600" : "";
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${toneClass}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <th className={`text-left px-2 py-1.5 font-medium ${className}`}>{children}</th>;
}
